import React, { useState, useEffect, useCallback } from 'react';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { trainingAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import ContentHeader from '../components/ContentHeader';
import DeleteIconButton from '../components/DeleteIconButton';
import './TrainingList.css';

function formatDate(d) {
  if (!d) return '—';
  return formatDisplayDate(d);
}

/** Normalize date for <input type="date"> (expects YYYY-MM-DD). API may send ISO or date string. */
function toDateInputValue(value) {
  if (value == null || value === '') return '';
  const s = typeof value === 'string' ? value.trim() : String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const TRAINING_TYPE_OPTIONS = [
  'Development-Typical',
  'Development-Atypical',
  'Assessment',
  'Intervention',
  'Families',
  'Other',
];

const SUPPORTING_DOC_OPTIONS = [
  'Certificate',
  'Syllabi',
  'University Letter',
  'Catalog Description',
  'Other',
];

/** Legacy staff info / Access column names → API fields */
const TEMP_TRAINING_DATE_FIELDS = [
  { key: 'intake_processing', label: 'Intake processing' },
  { key: 'certificate_tracking', label: 'Certificate tracking' },
  { key: 'intake_spieling', label: 'Intake spieling' },
  { key: 'ifsp_prep', label: 'IFSP prep' },
  { key: 'ifsp_processing', label: 'IFSP processing' },
  { key: 'ifsp_spieling', label: 'IFSP spieling' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'transition', label: 'Transition' },
];

function TrainingList() {
  const { tenantPath } = useTenantFromUrl();
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [window, setWindow] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialEditStaffId, setCredentialEditStaffId] = useState(null);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [tempTrainingRows, setTempTrainingRows] = useState([]);
  const [tempTrainingLoading, setTempTrainingLoading] = useState(false);
  const [showAddTempTraining, setShowAddTempTraining] = useState(false);
  const [tempTrainingEditStaffId, setTempTrainingEditStaffId] = useState(null);
  const [fullCredWindows, setFullCredWindows] = useState([]);
  const [fullCredLoading, setFullCredLoading] = useState(false);
  const [fullCredSelected, setFullCredSelected] = useState(new Set());
  const [fullCredNotifying, setFullCredNotifying] = useState(false);
  const [fullCredSort, setFullCredSort] = useState({ col: 'days', dir: 'asc' });
  const [showWindowForm, setShowWindowForm] = useState(false);
  const [windowForm, setWindowForm] = useState({ training_begin: '', training_end: '', comment: '' });
  const [windowFormSaving, setWindowFormSaving] = useState(false);
  const [windowFormError, setWindowFormError] = useState(null);
  const canManage = hasPermission('training.manage');
  const canMonitor = hasPermission('training.monitor');

  const loadStaff = useCallback(async () => {
    if (!canMonitor) return;
    try {
      const res = await staffAPI.getAll({ active: 'true' });
      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      setStaffList(data);
      if (data.length && !selectedStaffId) setSelectedStaffId(data[0].id);
    } catch {
      setStaffList([]);
    }
  }, [canMonitor, selectedStaffId]);

  const loadWindow = useCallback(async (staffId) => {
    if (staffId == null) {
      setWindow(null);
      return;
    }
    try {
      const res = await trainingAPI.getWindowByStaff(staffId);
      setWindow(res.data);
    } catch {
      setWindow(null);
    }
  }, []);

  const loadActivities = useCallback(async (staffId) => {
    setLoading(true);
    setError(null);
    try {
      const params = staffId != null ? { staff_id: staffId } : {};
      const res = await trainingAPI.reportActivity(params);
      setActivities(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (canMonitor && selectedStaffId != null) {
      loadWindow(selectedStaffId);
      loadActivities(selectedStaffId);
    } else if (!canMonitor) {
      trainingAPI.listWindows().then((res) => {
        const wins = Array.isArray(res.data) ? res.data : [];
        const myWin = wins[0] ?? null;
        setWindow(myWin);
        loadActivities(myWin?.staff_id ?? null);
      }).catch(() => {
        setWindow(null);
        loadActivities(null);
      });
    }
  }, [canMonitor, selectedStaffId, loadWindow, loadActivities]);

  const loadCredentials = useCallback(() => {
    setCredentialsLoading(true);
    const params = canMonitor ? {} : {};
    trainingAPI.listCredentials(params)
      .then((res) => setCredentials(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCredentials([]))
      .finally(() => setCredentialsLoading(false));
  }, [canMonitor]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const loadTempTraining = useCallback(() => {
    setTempTrainingLoading(true);
    trainingAPI.listTempTraining({})
      .then((res) => setTempTrainingRows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTempTrainingRows([]))
      .finally(() => setTempTrainingLoading(false));
  }, []);

  useEffect(() => {
    loadTempTraining();
  }, [loadTempTraining]);

  const loadFullCredWindows = useCallback(() => {
    if (!canMonitor) return;
    setFullCredLoading(true);
    trainingAPI.listFullCredentialWindows()
      .then((res) => setFullCredWindows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFullCredWindows([]))
      .finally(() => setFullCredLoading(false));
  }, [canMonitor]);

  useEffect(() => {
    loadFullCredWindows();
  }, [loadFullCredWindows]);

  const refreshActivities = useCallback(() => {
    if (canMonitor && selectedStaffId != null) loadActivities(selectedStaffId);
    else if (window?.staff_id != null) loadActivities(window.staff_id);
    else loadActivities(null);
  }, [canMonitor, selectedStaffId, window?.staff_id, loadActivities]);

  const openWindowForm = () => {
    setWindowForm({
      training_begin: toDateInputValue(window?.training_begin),
      training_end: toDateInputValue(window?.training_end),
      comment: window?.comment || '',
    });
    setWindowFormError(null);
    setShowWindowForm(true);
  };

  const handleWindowSave = async (e) => {
    e.preventDefault();
    setWindowFormSaving(true);
    setWindowFormError(null);
    try {
      const body = {
        training_begin: windowForm.training_begin || null,
        training_end: windowForm.training_end || null,
        comment: windowForm.comment?.trim() || null,
      };
      if (canMonitor && selectedStaffId != null) body.staff_id = selectedStaffId;
      const saved = await trainingAPI.upsertWindow(body);
      setWindow(saved.data ?? null);
      setShowWindowForm(false);
    } catch (err) {
      setWindowFormError(err.response?.data?.error || err.message || 'Failed to save window');
    } finally {
      setWindowFormSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this training activity?')) return;
    try {
      await trainingAPI.deleteActivity(id);
      refreshActivities();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const staffName = (row) => [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ') || '—';

  return (
    <div className="training-list-page">
      <ContentHeader
        title={canMonitor ? 'Training Activity' : 'My Training'}
        breadcrumbs={[{ label: 'Lists', to: tenantPath('/lists') }, { label: 'My Training' }]}
      />
      {error && <p className="training-list-error">{error}</p>}

      {canMonitor && (
        <nav className="training-section-nav" aria-label="On this page">
          <span className="training-section-nav-label">On this page</span>
          <ul className="training-section-nav-list">
            {[
              { id: 'training-section-activity', label: 'Training activity' },
              { id: 'training-section-credentials', label: 'Credential progress' },
              { id: 'training-section-full-cred', label: 'Full credential renewal' },
              { id: 'training-section-temp', label: 'Temp training milestones' },
            ].map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="training-section-nav-link">{item.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {canMonitor && staffList.length > 0 && (
        <div className="training-list-staff-filter">
          <label htmlFor="training-staff-select">Staff: </label>
          <select
            id="training-staff-select"
            value={selectedStaffId ?? ''}
            onChange={(e) => setSelectedStaffId(e.target.value ? Number(e.target.value) : null)}
          >
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>
                {[s.last_name, s.first_name].filter(Boolean).join(', ')}
              </option>
            ))}
          </select>
        </div>
      )}

      {window && !showWindowForm && (
        <div className="training-list-window">
          <strong>Training window:</strong> {formatDate(window.training_begin)} – {formatDate(window.training_end)}
          {window.comment && <span className="training-list-window-comment"> — {window.comment}</span>}
          {canManage && (
            <button type="button" className="training-list-window-edit-btn" onClick={openWindowForm}>
              Edit window
            </button>
          )}
        </div>
      )}

      {!window && !loading && !showWindowForm && (
        <p className="training-list-no-window">
          No training window set for this staff.
          {canManage && (
            <button type="button" className="training-list-window-edit-btn" onClick={openWindowForm}>
              Set window
            </button>
          )}
        </p>
      )}

      {showWindowForm && canManage && (
        <form className="training-list-window-form" onSubmit={handleWindowSave}>
          <strong>Training window</strong>
          {windowFormError && <p className="training-list-window-form-error">{windowFormError}</p>}
          <div className="training-list-window-form-row">
            <label>
              Begin
              <input type="date" value={windowForm.training_begin} onChange={(e) => setWindowForm((f) => ({ ...f, training_begin: e.target.value }))} />
            </label>
            <label>
              End
              <input type="date" value={windowForm.training_end} onChange={(e) => setWindowForm((f) => ({ ...f, training_end: e.target.value }))} />
            </label>
            <label className="training-list-window-form-comment">
              Comment
              <input type="text" value={windowForm.comment} onChange={(e) => setWindowForm((f) => ({ ...f, comment: e.target.value }))} placeholder="Optional" maxLength={200} />
            </label>
          </div>
          <div className="training-list-window-form-actions">
            <button type="button" className="btn-cancel" onClick={() => setShowWindowForm(false)} disabled={windowFormSaving}>Cancel</button>
            <button type="submit" className="btn-save" disabled={windowFormSaving}>{windowFormSaving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}

      <section id="training-section-activity" className="training-list-section">
        <h3 className="training-list-section-title">Training activity</h3>
        {canManage && (
          <div className="training-list-actions">
            <button type="button" className="btn btn-primary" onClick={() => setShowAddForm(true)}>
              + Add activity
            </button>
          </div>
        )}

        {showAddForm && (
          <AddActivityForm
            staffId={canMonitor ? selectedStaffId : window?.staff_id}
            onSave={() => { setShowAddForm(false); refreshActivities(); }}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        <div className="training-list-table-wrap">
          {loading ? (
            <p>Loading activities...</p>
          ) : (
            <table className="training-list-table">
              <thead>
                <tr>
                  {canMonitor && <th>Staff</th>}
                  <th>Activity</th>
                  <th>Date</th>
                  <th>Hours</th>
                  <th>Type</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {activities.length === 0 ? (
                  <tr><td colSpan={4 + (canMonitor ? 1 : 0) + (canManage ? 1 : 0)}>No activities in current window.</td></tr>
                ) : (
                  activities.map((row) => (
                    <tr
                      key={row.id}
                      className={canManage ? 'clickable-row' : ''}
                      onClick={canManage ? () => setEditingId(editingId === row.id ? null : row.id) : undefined}
                      role={canManage ? 'button' : undefined}
                      tabIndex={canManage ? 0 : undefined}
                      onKeyDown={canManage ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingId(editingId === row.id ? null : row.id); } } : undefined}
                    >
                      {canMonitor && <td>{staffName(row)}</td>}
                      <td>{row.activity || '—'}</td>
                      <td>{formatDate(row.training_date)}</td>
                      <td>{row.hours ?? '—'}</td>
                      <td>{row.type || '—'}</td>
                      {canManage && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <DeleteIconButton
                            className="list-btn-delete"
                            onClick={() => handleDelete(row.id)}
                          />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {editingId && (
        <EditActivityForm
          activityId={editingId}
          initial={activities.find((a) => a.id === editingId)}
          onSave={() => { setEditingId(null); refreshActivities(); }}
          onCancel={() => setEditingId(null)}
        />
      )}

      {(() => {
        const credentialsInProgress = credentials.filter((row) => !row.full_credential_start);
        const showCredentialSection = canMonitor || credentialsInProgress.length > 0;
        if (!showCredentialSection) return null;

        return (
          <section id="training-section-credentials" className="training-list-section training-credentials-section">
            <h3 className="training-list-section-title">Credential progress</h3>
            {canManage && (canMonitor || credentialsInProgress.length === 0) && (
              <div className="training-list-actions">
                <button type="button" className="btn btn-primary" onClick={() => { setShowAddCredential(true); setCredentialEditStaffId(null); }}>
                  {canMonitor ? 'Add credential record' : 'Add my credential record'}
                </button>
              </div>
            )}
            {credentialsLoading ? (
              <p>Loading credentials...</p>
            ) : (
              <div className="training-list-table-wrap">
                <table className="training-list-table">
                  <thead>
                    <tr>
                      {canMonitor && <th>Staff</th>}
                      <th>Days to Temp 2</th>
                      <th>Days to Full</th>
                      <th>Training window</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credentialsInProgress.length === 0 ? (
                      <tr><td colSpan={3 + (canMonitor ? 1 : 0)}>No credential records in progress.</td></tr>
                    ) : (
                      credentialsInProgress.map((row) => {
                        const staffLabel = [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ') || '—';
                        const windowLabel = row.training_begin || row.training_end
                          ? `${formatDate(row.training_begin)} – ${formatDate(row.training_end)}`
                          : '—';
                        const daysTemp2 = row.days_to_temp2 != null ? (row.days_to_temp2 === 'Completed' ? 'Completed' : Number(row.days_to_temp2)) : '—';
                        const daysFull = row.days_to_full != null ? Number(row.days_to_full) : '—';
                        const openEdit = () => { setCredentialEditStaffId(row.staff_id); setShowAddCredential(false); };
                        return (
                          <tr
                            key={row.id || row.staff_id}
                            className={canManage ? 'clickable-row' : ''}
                            onClick={canManage ? openEdit : undefined}
                            role={canManage ? 'button' : undefined}
                            tabIndex={canManage ? 0 : undefined}
                            onKeyDown={canManage ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(); } } : undefined}
                          >
                            {canMonitor && <td>{staffLabel}</td>}
                            <td>{daysTemp2}</td>
                            <td>{daysFull}</td>
                            <td>{windowLabel}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })()}

      {showAddCredential && (
        <CredentialForm
          mode="add"
          staffId={null}
          initial={{}}
          staffList={canMonitor ? staffList : []}
          addForStaffId={!canMonitor ? window?.staff_id : null}
          onSave={() => { setShowAddCredential(false); loadCredentials(); }}
          onCancel={() => setShowAddCredential(false)}
        />
      )}

      {credentialEditStaffId != null && !showAddCredential && (
        <CredentialForm
          mode="edit"
          staffId={credentialEditStaffId}
          initial={credentials.find((c) => c.staff_id === credentialEditStaffId)}
          staffList={[]}
          onSave={() => { setCredentialEditStaffId(null); loadCredentials(); }}
          onCancel={() => setCredentialEditStaffId(null)}
        />
      )}

      {canMonitor && (
        <section id="training-section-full-cred" className="training-list-section training-full-cred-section">
          <div className="training-full-cred-header">
            <h3 className="training-list-section-title">Full credential renewal</h3>
            <div className="training-full-cred-notify-bar training-full-cred-notify-bar-top">
              <button
                type="button"
                className="btn btn-primary"
                disabled={fullCredNotifying || fullCredSelected.size === 0}
                onClick={async () => {
                  const count = fullCredSelected.size;
                  setFullCredNotifying(true);
                  try {
                    await trainingAPI.notifyRecredential([...fullCredSelected]);
                    setFullCredSelected(new Set());
                    alert(`Notification sent to ${count} staff member${count === 1 ? '' : 's'}.`);
                  } catch (err) {
                    alert(err.response?.data?.error || err.message || 'Failed to send notifications');
                  } finally {
                    setFullCredNotifying(false);
                  }
                }}
              >
                {fullCredNotifying ? 'Sending…' : `Send notification${fullCredSelected.size > 0 ? ` (${fullCredSelected.size})` : ''}`}
              </button>
            </div>
          </div>
          {fullCredLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="training-list-table-wrap">
                <table className="training-list-table">
                  <thead>
                    <tr>
                      <th className="training-full-cred-th-check">
                        <input
                          type="checkbox"
                          checked={fullCredWindows.length > 0 && fullCredSelected.size === fullCredWindows.length}
                          onChange={(e) => setFullCredSelected(e.target.checked ? new Set(fullCredWindows.map((r) => r.staff_id)) : new Set())}
                          aria-label="Select all"
                        />
                      </th>
                      <th
                        className={`training-sortable-th${fullCredSort.col === 'staff' ? ' training-sort-active' : ''}`}
                        onClick={() => setFullCredSort((s) => s.col === 'staff' ? { col: 'staff', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: 'staff', dir: 'asc' })}
                      >
                        Staff {fullCredSort.col === 'staff' ? (fullCredSort.dir === 'asc' ? '▲' : '▼') : <span className="training-sort-idle">▲▼</span>}
                      </th>
                      <th>Training begin</th>
                      <th>Training end</th>
                      <th
                        className={`training-sortable-th${fullCredSort.col === 'days' ? ' training-sort-active' : ''}`}
                        onClick={() => setFullCredSort((s) => s.col === 'days' ? { col: 'days', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: 'days', dir: 'asc' })}
                      >
                        Days remaining {fullCredSort.col === 'days' ? (fullCredSort.dir === 'asc' ? '▲' : '▼') : <span className="training-sort-idle">▲▼</span>}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullCredWindows.length === 0 ? (
                      <tr><td colSpan={5}>No fully-credentialed staff found.</td></tr>
                    ) : (
                      [...fullCredWindows].sort((a, b) => {
                        const dir = fullCredSort.dir === 'asc' ? 1 : -1;
                        if (fullCredSort.col === 'staff') {
                          const la = [a.staff_last_name, a.staff_first_name].filter(Boolean).join(', ').toLowerCase();
                          const lb = [b.staff_last_name, b.staff_first_name].filter(Boolean).join(', ').toLowerCase();
                          return la < lb ? -dir : la > lb ? dir : 0;
                        }
                        const da = a.days_remaining != null ? Number(a.days_remaining) : Infinity;
                        const db = b.days_remaining != null ? Number(b.days_remaining) : Infinity;
                        return (da - db) * dir;
                      }).map((row) => {
                        const staffLabel = [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ') || '—';
                        const days = row.days_remaining != null ? Number(row.days_remaining) : null;
                        const daysCls = days == null ? '' : days <= 30 ? 'training-days-red' : days <= 90 ? 'training-days-orange' : 'training-days-green';
                        const daysLabel = days != null ? days : '—';
                        const isChecked = fullCredSelected.has(row.staff_id);
                        return (
                          <tr key={row.staff_id} className={isChecked ? 'training-full-cred-row-selected' : ''}>
                            <td className="training-full-cred-td-check">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const next = new Set(fullCredSelected);
                                  e.target.checked ? next.add(row.staff_id) : next.delete(row.staff_id);
                                  setFullCredSelected(next);
                                }}
                                aria-label={`Select ${staffLabel}`}
                              />
                            </td>
                            <td>{staffLabel}</td>
                            <td>{formatDate(row.training_begin)}</td>
                            <td>{formatDate(row.training_end)}</td>
                            <td className={daysCls}>{daysLabel}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {fullCredSelected.size > 0 && (
                <div className="training-full-cred-notify-bar">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={fullCredNotifying}
                    onClick={async () => {
                      const count = fullCredSelected.size;
                      setFullCredNotifying(true);
                      try {
                        await trainingAPI.notifyRecredential([...fullCredSelected]);
                        setFullCredSelected(new Set());
                        alert(`Notification sent to ${count} staff member${count === 1 ? '' : 's'}.`);
                      } catch (err) {
                        alert(err.response?.data?.error || err.message || 'Failed to send notifications');
                      } finally {
                        setFullCredNotifying(false);
                      }
                    }}
                  >
                    {fullCredNotifying ? 'Sending…' : `Send notification (${fullCredSelected.size})`}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {(() => {
        const showTempTrainingSection = canMonitor || canManage || tempTrainingRows.length > 0;
        if (!showTempTrainingSection) return null;
        return (
          <section id="training-section-temp" className="training-list-section training-temp-training-section">
            <h3 className="training-list-section-title">Temp training milestones</h3>
            <p className="training-list-section-hint">
              Milestone dates for staff on a temporary credential (legacy “Edit/View Temp Training”).
            </p>
            {canManage && (
              <div className="training-list-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { setShowAddTempTraining(true); setTempTrainingEditStaffId(null); }}
                >
                  {canMonitor ? 'Add temp training record' : 'Add my temp training record'}
                </button>
              </div>
            )}
            {tempTrainingLoading ? (
              <p>Loading temp training...</p>
            ) : (
              <div className="training-list-table-wrap">
                <table className="training-list-table training-temp-training-table">
                  <thead>
                    <tr>
                      {canMonitor && <th>Staff</th>}
                      {TEMP_TRAINING_DATE_FIELDS.map(({ label }) => (
                        <th key={label}>{label}</th>
                      ))}
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tempTrainingRows.length === 0 ? (
                      <tr>
                        <td colSpan={TEMP_TRAINING_DATE_FIELDS.length + 1 + (canMonitor ? 1 : 0)}>
                          No temp training milestone records yet.
                        </td>
                      </tr>
                    ) : (
                      tempTrainingRows.map((row) => {
                        const staffLabel = [row.staff_last_name, row.staff_first_name].filter(Boolean).join(', ') || '—';
                        const openEdit = () => {
                          if (!canManage) return;
                          setTempTrainingEditStaffId(row.staff_id);
                          setShowAddTempTraining(false);
                        };
                        return (
                          <tr
                            key={row.id || row.staff_id}
                            className={canManage ? 'clickable-row' : ''}
                            onClick={canManage ? openEdit : undefined}
                            role={canManage ? 'button' : undefined}
                            tabIndex={canManage ? 0 : undefined}
                            onKeyDown={canManage ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(); }
                            } : undefined}
                          >
                            {canMonitor && <td>{staffLabel}</td>}
                            {TEMP_TRAINING_DATE_FIELDS.map(({ key }) => (
                              <td key={key}>{formatDate(row[key])}</td>
                            ))}
                            <td>{row.training_comment?.trim() ? row.training_comment : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })()}

      {showAddTempTraining && (
        <TempTrainingForm
          mode="add"
          staffId={null}
          initial={{}}
          staffList={canMonitor ? staffList : []}
          addForStaffId={!canMonitor ? window?.staff_id : null}
          onSave={() => { setShowAddTempTraining(false); loadTempTraining(); }}
          onCancel={() => setShowAddTempTraining(false)}
        />
      )}

      {tempTrainingEditStaffId != null && !showAddTempTraining && (
        <TempTrainingForm
          mode="edit"
          staffId={tempTrainingEditStaffId}
          initial={tempTrainingRows.find((r) => r.staff_id === tempTrainingEditStaffId)}
          staffList={[]}
          onSave={() => { setTempTrainingEditStaffId(null); loadTempTraining(); }}
          onCancel={() => setTempTrainingEditStaffId(null)}
        />
      )}
    </div>
  );
}

function AddActivityForm({ staffId, onSave, onCancel }) {
  const [activity, setActivity] = useState('');
  const [training_date, setTrainingDate] = useState('');
  const [hours, setHours] = useState('');
  const [type, setType] = useState('');
  const [supporting_doc, setSupportingDoc] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activity.trim() || !training_date || hours === '' || Number(hours) < 0) {
      alert('Activity, date, and hours are required.');
      return;
    }
    setSubmitting(true);
    try {
      await trainingAPI.createActivity({
        staff_id: staffId ?? undefined,
        activity: activity.trim(),
        training_date,
        hours: Number(hours),
        type: type.trim() || undefined,
        supporting_doc: supporting_doc.trim() || undefined,
      });
      onSave();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="training-form-overlay">
      <form className="training-activity-form" onSubmit={handleSubmit}>
        <h3>Add training activity</h3>
        <label>Activity *</label>
        <input type="text" value={activity} onChange={(e) => setActivity(e.target.value)} required />
        <label>Training date *</label>
        <input type="date" value={training_date} onChange={(e) => setTrainingDate(e.target.value)} required />
        <label>Hours *</label>
        <input type="number" step="0.25" min="0" value={hours} onChange={(e) => setHours(e.target.value)} required />
        <label>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">— Select —</option>
          {TRAINING_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <label>Supporting doc</label>
        <select value={supporting_doc} onChange={(e) => setSupportingDoc(e.target.value)}>
          <option value="">— Select —</option>
          {SUPPORTING_DOC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="training-form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

function EditActivityForm({ activityId, initial, onSave, onCancel }) {
  const [activity, setActivity] = useState(initial?.activity ?? '');
  const [training_date, setTrainingDate] = useState(toDateInputValue(initial?.training_date) ?? '');
  const [hours, setHours] = useState(initial?.hours ?? '');
  const [type, setType] = useState(initial?.type ?? '');
  const [supporting_doc, setSupportingDoc] = useState(initial?.supporting_doc ?? '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activity.trim() || !training_date || hours === '' || Number(hours) < 0) {
      alert('Activity, date, and hours are required.');
      return;
    }
    setSubmitting(true);
    try {
      await trainingAPI.updateActivity(activityId, {
        activity: activity.trim(),
        training_date,
        hours: Number(hours),
        type: type.trim() || undefined,
        supporting_doc: supporting_doc.trim() || undefined,
      });
      onSave();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="training-form-overlay">
      <form className="training-activity-form" onSubmit={handleSubmit}>
        <h3>Edit training activity</h3>
        <label>Activity *</label>
        <input type="text" value={activity} onChange={(e) => setActivity(e.target.value)} required />
        <label>Training date *</label>
        <input type="date" value={training_date} onChange={(e) => setTrainingDate(e.target.value)} required />
        <label>Hours *</label>
        <input type="number" step="0.25" min="0" value={hours} onChange={(e) => setHours(e.target.value)} required />
        <label>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">— Select —</option>
          {TRAINING_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <label>Supporting doc</label>
        <select value={supporting_doc} onChange={(e) => setSupportingDoc(e.target.value)}>
          <option value="">— Select —</option>
          {SUPPORTING_DOC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="training-form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

const CREDENTIAL_DATE_FIELDS = [
  { key: 'application_sent', label: 'Application sent' },
  { key: 'fingerprint_receipt', label: 'Fingerprint receipt' },
  { key: 'temp1_credential_start', label: 'Temp 1 credential start' },
  { key: 'module_set_1_complete', label: 'Module set 1 complete' },
  { key: 'module_set_2_complete', label: 'Module set 2 complete' },
  { key: 'system_overview_live_complete', label: 'System overview live complete' },
  { key: 'temp2_credential_requested', label: 'Temp 2 credential requested' },
  { key: 'temp2_credential_start', label: 'Temp 2 credential start' },
  { key: 'full_credential_requested', label: 'Full credential requested' },
  { key: 'full_credential_start', label: 'Full credential start' },
];

function CredentialForm({ mode, staffId, initial, staffList, addForStaffId, onSave, onCancel }) {
  const [selectedStaffId, setSelectedStaffId] = useState(
    mode === 'add' ? (addForStaffId != null ? addForStaffId : staffId || '') : staffId
  );
  const [fields, setFields] = useState(() => {
    const state = {};
    CREDENTIAL_DATE_FIELDS.forEach(({ key }) => {
      state[key] = toDateInputValue(initial?.[key]) ?? '';
    });
    state.comment = initial?.comment ?? '';
    return state;
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode !== 'edit' || !staffId) return;
    if (initial?.id) return;
    trainingAPI.getCredentialsByStaff(staffId)
      .then((res) => {
        const data = res.data || {};
        setFields((prev) => {
          const next = { ...prev };
          CREDENTIAL_DATE_FIELDS.forEach(({ key }) => {
            next[key] = toDateInputValue(data[key]) ?? '';
          });
          next.comment = data.comment ?? '';
          return next;
        });
      })
      .catch(() => {});
  }, [mode, staffId, initial?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveStaffId = mode === 'add'
      ? (addForStaffId != null ? addForStaffId : (selectedStaffId ? Number(selectedStaffId) : null))
      : staffId;
    if (mode === 'add' && effectiveStaffId == null) {
      alert('Select a staff member.');
      return;
    }
    setSubmitting(true);
    try {
      const body = { staff_id: effectiveStaffId };
      CREDENTIAL_DATE_FIELDS.forEach(({ key }) => {
        body[key] = fields[key]?.trim() || null;
      });
      body.comment = fields.comment?.trim() || null;
      await trainingAPI.upsertCredentials(body);
      onSave();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="training-form-overlay">
      <form className="training-activity-form training-credential-form" onSubmit={handleSubmit}>
        <h3>{mode === 'add' ? 'Add credential record' : 'Edit credential progress'}</h3>
        {mode === 'add' && addForStaffId == null && staffList.length > 0 && (
          <div className="credential-form-row-full">
            <label htmlFor="cred-form-staff">Staff *</label>
            <select
              id="cred-form-staff"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              required
            >
              <option value="">— Select —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.last_name, s.first_name].filter(Boolean).join(', ')}
                </option>
              ))}
            </select>
          </div>
        )}
        {CREDENTIAL_DATE_FIELDS.map(({ key, label }) => (
          <div key={key} className="credential-form-row">
            <label htmlFor={`cred-form-${key}`}>{label}</label>
            <input
              id={`cred-form-${key}`}
              type="date"
              value={fields[key] ?? ''}
              onChange={(e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          </div>
        ))}
        <div className="credential-form-row-full">
          <label htmlFor="cred-form-comment">Comment</label>
          <input
            id="cred-form-comment"
            type="text"
            value={fields.comment ?? ''}
            onChange={(e) => setFields((prev) => ({ ...prev, comment: e.target.value }))}
          />
        </div>
        <div className="training-form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

function TempTrainingForm({ mode, staffId, initial, staffList, addForStaffId, onSave, onCancel }) {
  const [selectedStaffId, setSelectedStaffId] = useState(
    mode === 'add' ? (addForStaffId != null ? addForStaffId : staffId || '') : staffId
  );
  const [fields, setFields] = useState(() => {
    const state = {};
    TEMP_TRAINING_DATE_FIELDS.forEach(({ key }) => {
      state[key] = toDateInputValue(initial?.[key]) ?? '';
    });
    state.training_comment = initial?.training_comment ?? '';
    return state;
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode !== 'edit' || !staffId) return;
    if (initial?.id) return;
    trainingAPI.getTempTrainingByStaff(staffId)
      .then((res) => {
        const data = res.data || {};
        setFields((prev) => {
          const next = { ...prev };
          TEMP_TRAINING_DATE_FIELDS.forEach(({ key }) => {
            next[key] = toDateInputValue(data[key]) ?? '';
          });
          next.training_comment = data.training_comment ?? '';
          return next;
        });
      })
      .catch(() => {});
  }, [mode, staffId, initial?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveStaffId = mode === 'add'
      ? (addForStaffId != null ? addForStaffId : (selectedStaffId ? Number(selectedStaffId) : null))
      : staffId;
    if (mode === 'add' && effectiveStaffId == null) {
      alert('Select a staff member.');
      return;
    }
    setSubmitting(true);
    try {
      const body = { staff_id: effectiveStaffId };
      TEMP_TRAINING_DATE_FIELDS.forEach(({ key }) => {
        body[key] = fields[key]?.trim() || null;
      });
      body.training_comment = fields.training_comment?.trim() || null;
      await trainingAPI.upsertTempTraining(body);
      onSave();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="training-form-overlay">
      <form className="training-activity-form training-credential-form" onSubmit={handleSubmit}>
        <h3>{mode === 'add' ? 'Add temp training record' : 'Edit temp training milestones'}</h3>
        {mode === 'add' && addForStaffId == null && staffList.length > 0 && (
          <div className="credential-form-row-full">
            <label htmlFor="temp-training-form-staff">Staff *</label>
            <select
              id="temp-training-form-staff"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              required
            >
              <option value="">— Select —</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.last_name, s.first_name].filter(Boolean).join(', ')}
                </option>
              ))}
            </select>
          </div>
        )}
        {TEMP_TRAINING_DATE_FIELDS.map(({ key, label }) => (
          <div key={key} className="credential-form-row">
            <label htmlFor={`temp-training-form-${key}`}>{label}</label>
            <input
              id={`temp-training-form-${key}`}
              type="date"
              value={fields[key] ?? ''}
              onChange={(e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          </div>
        ))}
        <div className="credential-form-row-full">
          <label htmlFor="temp-training-form-comment">Comment</label>
          <input
            id="temp-training-form-comment"
            type="text"
            value={fields.training_comment ?? ''}
            onChange={(e) => setFields((prev) => ({ ...prev, training_comment: e.target.value }))}
          />
        </div>
        <div className="training-form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

export default TrainingList;
